import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(6px)",
    },
    /* Small centered card for loading / error state */
    paperSmall: {
        backgroundColor: "#ffffff",
        boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        padding: "32px",
        borderRadius: "16px",
        width: "480px",
        maxWidth: "90%",
        outline: "none",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "24px",
        border: "1px solid rgba(0,0,0,0.08)",
    },
    /* Large card that shows exam data inside the popup */
    paperLarge: {
        backgroundColor: "#ffffff",
        boxShadow: "none",
        borderRadius: 0,
        width: "100vw",
        height: "100vh",
        outline: "none",
        display: "flex",
        flexDirection: "column",
        border: "none",
        overflow: "hidden",
    },
    /* Fixed header bar inside the large popup */
    popupHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        borderBottom: "2px solid #00577B",
        backgroundColor: "#f8fafc",
        flexShrink: 0,
    },
    headerLeft: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        flex: 1,
        minWidth: 0,
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexShrink: 0,
    },
    /* Scrollable body area */
    popupBody: {
        flex: 1,
        overflowY: "auto",
        padding: "24px",
        "&::-webkit-scrollbar": {
            width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#00577B",
            borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
        },
    },
    /* Paper detail summary chips row */
    detailRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
        marginBottom: "20px",
        padding: "14px 18px",
        backgroundColor: "#f5f7fa",
        borderRadius: "10px",
        border: "1px solid #e0e7ee",
    },
    detailChip: {
        fontWeight: 600,
        fontSize: "0.82rem",
    },
    loaderContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
        margin: "10px 0",
    },
    statusIconSuccess: {
        fontSize: "64px",
        color: "#2e7d32",
        animation: "$scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    },
    statusIconError: {
        fontSize: "64px",
        color: "#d32f2f",
        animation: "$scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    },
    title: {
        fontFamily: "Outfit, Roboto, sans-serif",
        fontWeight: 700,
        color: "#212121",
        fontSize: "1.6rem",
    },
    titleSmall: {
        fontFamily: "Outfit, Roboto, sans-serif",
        fontWeight: 700,
        color: "#212121",
        fontSize: "1.15rem",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    subtitle: {
        fontFamily: "Outfit, Roboto, sans-serif",
        color: "#666",
        fontSize: "0.85rem",
    },
    text: {
        fontFamily: "Outfit, Roboto, sans-serif",
        color: "#666666",
        fontSize: "1.05rem",
        lineHeight: "1.5",
    },
    button: {
        fontFamily: "Outfit, Roboto, sans-serif",
        fontWeight: 600,
        textTransform: "none",
        padding: "10px 28px",
        borderRadius: "8px",
        boxShadow: "none",
        fontSize: "1rem",
        "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        },
    },
    btnPrimary: {
        background: "linear-gradient(45deg, #00577B 30%, #0087BE 90%)",
        color: "white",
        "&:hover": {
            background: "linear-gradient(45deg, #004663 30%, #006D9A 90%)",
        },
    },
    btnUpload: {
        background: "linear-gradient(45deg, #2e7d32 30%, #43a047 90%)",
        color: "white",
        "&:hover": {
            background: "linear-gradient(45deg, #1b5e20 30%, #2e7d32 90%)",
        },
    },
    "@keyframes scaleIn": {
        "0%": { transform: "scale(0)", opacity: 0 },
        "100%": { transform: "scale(1)", opacity: 1 },
    },

    /* ---------- Paper display styles ---------- */
    subjectPaper: {
        padding: "20px",
        marginBottom: "24px",
        borderRadius: "12px",
    },
    subjectHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "2px solid #00577B",
        paddingBottom: "10px",
        marginBottom: "12px",
    },
    totalChip: {
        backgroundColor: "#00577B",
        color: "#fff",
        fontWeight: 600,
    },
    totalChipSmall: {
        backgroundColor: "#0087BE",
        color: "#fff",
        fontWeight: 600,
    },
    subsectionBlock: {
        marginBottom: "20px",
        paddingLeft: "8px",
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "#f5f7fa",
        padding: "8px 12px",
        borderRadius: "8px",
        marginBottom: "12px",
    },
    questionCard: {
        padding: "14px",
        marginBottom: "10px",
        borderRadius: "10px",
        border: "1px solid #eee",
    },
    questionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px",
        marginBottom: "8px",
    },
    qNumber: {
        fontWeight: 700,
        color: "#00577B",
    },
    marksInfo: {
        display: "flex",
        gap: "6px",
        flexWrap: "wrap",
    },
    chipPositive: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        fontWeight: 600,
    },
    chipNegative: {
        backgroundColor: "#ffebee",
        color: "#c62828",
        fontWeight: 600,
    },
    chipPartial: {
        backgroundColor: "#fff8e1",
        color: "#f57f17",
        fontWeight: 600,
    },
    langBlock: {
        borderTop: "1px dashed #ddd",
        paddingTop: "10px",
        marginTop: "10px",
    },
    langLabel: {
        fontWeight: 700,
        fontSize: "0.8rem",
        color: "#0087BE",
        textTransform: "uppercase",
        marginBottom: "6px",
    },
    qnsContent: {
        marginBottom: "8px",
        "& img": { maxWidth: "100%" },
    },
    optionsGrid: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    optionItem: {
        display: "flex",
        gap: "8px",
        padding: "6px 10px",
        borderRadius: "6px",
        backgroundColor: "#fafafa",
        "& img": { maxWidth: "100%" },
    },
    correctOption: {
        backgroundColor: "#e8f5e9",
        border: "1px solid #2e7d32",
    },
    optionLabel: {
        fontWeight: 700,
    },
    fillBlankAnswer: {
        padding: "10px 14px",
        borderRadius: "8px",
        backgroundColor: "#e8f5e9",
        border: "1px solid #2e7d32",
        fontSize: "1rem",
        display: "inline-block",
    },
}));

export default useStyles;
